import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  Document,
  DocumentListItem,
  PaginatedDocuments,
  DocumentUploadResponse,
  PresignedUrlRequest,
  PresignedUrlResponse,
  ConfirmUploadRequest,
  DownloadUrlResponse,
  DeleteDocumentResponse,
  DocumentFilters,
  ParsingStatusResponse
} from '../models/document.models';

/**
 * Document Service
 * Handles all document-related API calls
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private http = inject(HttpClient);
  private readonly API_URL = `${environment.apiUrl}/documents`;

  /**
   * Upload document using multipart form data (for files <= 10MB)
   */
  uploadDocument(
    file: File,
    templateId?: string,
    tags?: string[]
  ): Observable<{ progress: number; response?: DocumentUploadResponse }> {
    const formData = new FormData();
    formData.append('file', file);
    if (templateId) {
      formData.append('template_id', templateId);
    }
    if (tags && tags.length > 0) {
      formData.append('tags', tags.join(','));
    }

    return this.http.post<DocumentUploadResponse>(`${this.API_URL}/upload`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
          return { progress };
        } else if (event.type === HttpEventType.Response) {
          return { progress: 100, response: event.body! };
        }
        return { progress: 0 };
      }),
      catchError(error => {
        console.error('Upload error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generate presigned URL for large file upload (> 10MB, <= 5GB)
   */
  generatePresignedUrl(request: PresignedUrlRequest): Observable<PresignedUrlResponse> {
    return this.http.post<PresignedUrlResponse>(`${this.API_URL}/upload-url`, request);
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  uploadToS3(url: string, fields: Record<string, string>, file: File): Observable<number> {
    const formData = new FormData();
    
    // Add all presigned fields first
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });
    
    // Add file last
    formData.append('file', file);

    return this.http.post(url, formData, {
      reportProgress: true,
      observe: 'events',
      responseType: 'text'
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress) {
          return event.total ? Math.round((100 * event.loaded) / event.total) : 0;
        } else if (event.type === HttpEventType.Response) {
          return 100;
        }
        return 0;
      })
    );
  }

  /**
   * Confirm upload completion after S3 upload
   */
  confirmUpload(documentId: string, request: ConfirmUploadRequest): Observable<any> {
    return this.http.post(`${this.API_URL}/${documentId}/confirm-upload`, request);
  }

  /**
   * Get paginated list of documents with filters
   */
  getDocuments(filters: DocumentFilters): Observable<PaginatedDocuments> {
    let params = new HttpParams()
      .set('page', filters.page.toString())
      .set('page_size', filters.page_size.toString());

    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.template_id) {
      params = params.set('template_id', filters.template_id);
    }
    if (filters.search) {
      params = params.set('search', filters.search);
    }

    return this.http.get<PaginatedDocuments>(this.API_URL, { params });
  }

  /**
   * Get document by ID
   */
  getDocument(documentId: string): Observable<Document> {
    return this.http.get<Document>(`${this.API_URL}/${documentId}`);
  }

  /**
   * Get download URL for document
   */
  getDownloadUrl(documentId: string): Observable<DownloadUrlResponse> {
    return this.http.get<DownloadUrlResponse>(`${this.API_URL}/${documentId}/download`);
  }

  getFilterTemplates(): Observable<{ templates: Array<{ id: string; name: string; deleted: boolean }> }> {
    return this.http.get<{ templates: Array<{ id: string; name: string; deleted: boolean }> }>(
      `${this.API_URL}/filter-templates`
    );
  }

  /**
   * Delete document (soft delete by default)
   */
  deleteDocument(documentId: string, permanent: boolean = false): Observable<DeleteDocumentResponse> {
    const params = new HttpParams().set('permanent', permanent.toString());
    return this.http.delete<DeleteDocumentResponse>(`${this.API_URL}/${documentId}`, { params });
  }

  /**
   * Get parsing status for a document
   */
  getParsingStatus(documentId: string): Observable<ParsingStatusResponse> {
    return this.http.get<ParsingStatusResponse>(`${environment.apiUrl}/parsing/${documentId}/parsing-status`);
  }

  reparseDocument(documentId: string, templateId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${environment.apiUrl}/parsing/${documentId}/reparse`,
      {
        template_id: templateId,
        async_mode: true
      }
    );
  }
}

